/**
 * Multi-site configuration loader
 *
 * Зарежда конфигурацията на базата на NEXT_PUBLIC_SITE_ID env variable.
 * По подразбиране: "travogue"
 *
 * Използване:
 *   import { getSiteConfig, getSiteId } from '@/config/sites';
 *   const site = getSiteConfig();
 *   console.log(site.branding.name); // "Travogue" или "BabyVogue"
 */

import type { SiteConfig, SiteId, SiteTheme } from './types';
import { travogueConfig } from './travogue';
import { babyVogueConfig } from './babyvogue';

// Регистър на всички сайтове
const SITE_CONFIGS: Record<SiteId, SiteConfig> = {
  travogue: travogueConfig,
  babyvogue: babyVogueConfig,
};

/**
 * Връща текущия site ID от env.
 */
export function getSiteId(): SiteId {
  // Fallback-ът е travogue (както казват и docblock-ът, и warning-ът долу).
  // До 09.2026 тук пишеше 'babyvogue' — среда без NEXT_PUBLIC_SITE_ID тихо
  // вдигаше грешния сайт. CI/прод винаги подават ID-то явно (deploy.yml matrix).
  const id = (process.env.NEXT_PUBLIC_SITE_ID || 'travogue') as SiteId;
  if (!(id in SITE_CONFIGS)) {
    console.warn(`Unknown NEXT_PUBLIC_SITE_ID="${id}", falling back to "travogue"`);
    return 'travogue';
  }
  return id;
}

// Singleton cache
let _cachedConfig: SiteConfig | null = null;

/**
 * Връща конфигурацията на ДРУГИЯ сайт — за превключвателя в хедъра.
 * Регистърът е с точно два сайта, тъй че „другият" е еднозначен.
 */
export function getOtherSiteConfig(): SiteConfig {
  return getSiteId() === 'travogue' ? SITE_CONFIGS.babyvogue : SITE_CONFIGS.travogue;
}

/**
 * Връща пълната конфигурация за текущия сайт.
 */
export function getSiteConfig(): SiteConfig {
  if (!_cachedConfig) {
    _cachedConfig = SITE_CONFIGS[getSiteId()];
  }
  return _cachedConfig;
}

/**
 * Генерира CSS custom properties обект за React style prop на <html>.
 *
 * Пример:
 *   { '--primary-50': '#f0f9f6', '--primary-100': '#dcf2ed', ... }
 */
export function getThemeCSSVars(theme?: SiteTheme): Record<string, string> {
  const t = theme || getSiteConfig().theme;
  // Per-site CDN база за текстурите, ползвани в *.module.scss през var(...).
  // cdnBase е per-site, затова babyvogue тегли от своя CDN, не от travogue.
  const cdn = `${getSiteConfig().branding.cdnBase}/assets`;
  return {
    '--primary-50': t.primary50,
    '--primary-100': t.primary100,
    '--primary-200': t.primary200,
    '--primary-300': t.primary300,
    '--primary-400': t.primary400,
    '--primary-500': t.primary500,
    '--primary-600': t.primary600,
    '--primary-700': t.primary700,
    '--primary-800': t.primary800,
    '--primary-900': t.primary900,
    '--primary-glow': t.primaryGlow,
    '--dynamic-color': t.accentColor,
    '--asset-bgr': `url("${cdn}/backgrounds/bgr.webp")`,
    '--asset-noise': `url("${cdn}/textures/noise.webp")`,
    // webp, не png: това е декоративен overlay (mix-blend-mode: multiply) върху
    // голямата корица. PNG-то беше 59.3 KB — 6% от началната страница — при 9.2 KB
    // за същите пиксели в webp q90. Файлът е качен и в двата bucket-а.
    '--asset-book-mockup': `url("${cdn}/textures/book-mockup.webp")`,
    '--asset-shadow': `url("${cdn}/backgrounds/shadow.webp")`,
    // Панделката на ваучера. Ползва се като CSS маска, тъй че цветът идва от
    // темата — файлът е само формата.
    '--asset-ribbon': `url("${cdn}/voucher/ribbon.svg")`,
  } as Record<string, string>;
}

/**
 * Проверява дали текущият сайт е конкретен.
 */
export function isSite(id: SiteId): boolean {
  return getSiteId() === id;
}

/**
 * Дефолтна Open Graph картинка за страници без собствена. Връща масив във
 * формата на Next `openGraph.images`. Ползвай в metadata на страници, които
 * нямат специфична картинка (в App Router openGraph НЕ се наследява по поле –
 * дете, което дефинира openGraph, трябва само да зададе images).
 */
export function defaultOgImages(): Array<{
  url: string;
  width: number;
  height: number;
  alt: string;
}> {
  const site = getSiteConfig();
  return [
    {
      url: site.assets.ogImage,
      // Реалните размери на og-image.png. За най-чисто превю (1.91:1) идеалът е
      // 1200×630 – ако преекспортнете картинката, сменете и тук на 630.
      width: 1200,
      height: 683,
      alt: site.branding.name,
    },
  ];
}

// Re-export types
export type { SiteConfig, SiteId, SiteTheme } from './types';
